"use client";
import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function IndustryHero({ data = {}, industryId }) {
  const title = data?.title || "Solutions";
  const heroText = data?.heroText || `Premium Odoo Software for ${title}`;
  const heroSubtext = data?.heroSubtext || `Optimize your ${title} operations with Odoo's fully integrated suite of business applications.`;
  const heroIcon = data?.heroIcon || "/odoo/icons/green_thumbs-up.svg";
  const heroImage = data?.heroImage || "/odoo/unsplash/app/crm-pipeline.jpg";

  return (
    <section className="relative pt-32 pb-20 px-6 sm:px-12 lg:px-24">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#f1833b]/5 rounded-full blur-[140px] -z-0" />
      <div className="container mx-auto relative z-10">
        <motion.div
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          <div>
            <h1 className={`leading-tight mb-8 text-white ${caveat.className}`}>
              <span 
                className="text-3xl md:text-4xl block font-normal mb-2"
                dangerouslySetInnerHTML={{
                  __html: heroText.includes("for")
                    ? `${heroText.split("for")[0]} for`
                    : heroText
                }}
              />
              <span className="text-5xl md:text-7xl lg:text-[80px] block font-bold leading-none">
                {title}
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-10 max-w-xl leading-relaxed">
              {heroSubtext.includes(",") ? (
                <>
                  <strong className="text-white">{heroSubtext.split(",")[0]},</strong>
                  {heroSubtext.substring(heroSubtext.indexOf(",") + 1)}
                </>
              ) : (
                heroSubtext
              )}
            </p>

            <div className="flex flex-wrap items-center gap-6">
              <Link href={`/odoo/start/${industryId || title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                <button className="px-8 py-3 bg-[#f1833b] text-white font-bold rounded-xl hover:bg-[#e06b24] transition-all shadow-lg text-base hover:scale-105 active:scale-95">
                  Start now
                </button>
              </Link>

              <a 
                href="https://cal.com/codeqlik/odoo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-3 bg-white/10 border border-white/20 text-gray-200 font-bold rounded-xl hover:bg-white/20 transition-all text-base flex items-center gap-2 hover:scale-105 active:scale-95"
              >
                Meet an advisor
              </a>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md aspect-square">
              {heroIcon && (
                <motion.div
                  animate={{ rotate: [15, 25, 15] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="absolute -top-6 -right-6 w-24 h-24 z-20 d-none d-lg-block drop-shadow-xl"
                >
                  <Image
                    src={heroIcon}
                    width={96}
                    height={96}
                    className="w-full h-full object-contain"
                    alt=""
                    priority
                  />
                </motion.div>
              )}
              <div className="w-full h-full rounded-full overflow-hidden border-[12px] border-white/5 shadow-2xl bg-white/10 relative">
                {heroImage && (
                  <Image 
                    src={heroImage} 
                    fill 
                    sizes="(max-width: 768px) 100vw, 40vw"
                    className="object-cover" 
                    alt={title}
                    priority
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
